import * as React from 'react'
import PropTypes from 'prop-types'
import {useRouter} from 'next/router'

import {sendEmail, getIp} from '@lib/services'
import {acceptConsent} from '@lib/utils/consent'
import {useAsync} from '@lib/hooks'
import {useRecaptcha} from '@lib/recaptcha'
import {logEvent, useUTMContext} from '@lib/gtm'
import {validateEmail} from '@lib/helpers'

import {MarketingCheckbox, PrivacyNotice} from '@components/forms'

const ContactForm = () => {
  const router = useRouter()
  const [accept, setAccept] = React.useState(false)
  const {run: runSendEmail, isLoading, isSuccess} = useAsync(sendEmail)
  const {run: runGetIp} = useAsync(getIp)
  const {isRecaptchaValidated} = useRecaptcha()
  const ref = React.useRef()

  const {data: utm} = useUTMContext()

  React.useEffect(() => {
    if (isSuccess && ref.current) {
      ref.current.reset()
      setAccept(false)

      logEvent({
        ga: {
          category: 'Submit Form Success',
          action: 'Submit Form Success',
          label: router.pathname.replace('/', '').replaceAll('-', ' '),
        },
        fb: {
          event: 'Lead',
        },
      })
    }
  }, [isSuccess])

  const handleSubmit = async (e) => {
    e.preventDefault()

    const {email, message, topic, phone, accept} = e.target.elements
    const ip = await runGetIp({})

    const emailData = {
      to: topic.value === 'jobs' ? 'hr@datawow.io' : process.env.SALES_EMAIL,
      subject: `[${window.location.host}] ${email.value} wants to contact with you`,
      message: `
        Email: ${email.value}
        Tel: ${phone.value}
        Message: ${message.value}
        IP Address: ${ip}
      `,
    }

    const reqBody = {
      email: email.value,
      message: message.value,
      template: topic.value === 'jobs' ? 'apply_job' : 'products_and_services',
      phone: phone.value,
      url: window.location.href,
      marketing_consent: accept.checked,
    }

    const res = await runSendEmail({
      emailData: {...emailData, path: process.env.EMAIL_API_URL},
      reqBody: reqBody,
      utm,
    })

    if (res.status === 200 && accept.checked) {
      await acceptConsent({
        apiKey: process.env.CONSENT_WOW_CONTACT_API_KEY,
        purposeId: process.env.CONSENT_WOW_MARKETING_CONSENT_PURPOSE_ID,
        data: {
          ...reqBody,
          phone_number: phone.value,
        },
      })
    }
  }

  return (
    <React.Fragment>
      <form ref={ref} className='Contact__form' onSubmit={handleSubmit}>
        <select name='topic' className='Input Input--select'>
          <option value='services'>สินค้าและบริการ</option>
          <option value='jobs'>สมัครงาน</option>
        </select>

        <input
          type='email'
          className='Input'
          placeholder='อีเมล์'
          name='email'
          onBlur={validateEmail}
          required
        />

        <input
          type='tel'
          className='Input'
          placeholder='เบอร์โทรศัพท์'
          name='phone'
          pattern='\d{10}'
          title='Please enter a 10-digit phone number'
          required
        />

        <textarea
          className='Input'
          placeholder='ข้อความ'
          rows={5}
          name='message'
        />

        <MarketingCheckbox
          name='accept'
          className='Contact__form__checkbox'
          isChecked={accept}
          onClick={() => {
            setAccept((prev) => !prev)
          }}
        />

        <button
          className='Button'
          disabled={isLoading || !isRecaptchaValidated}
        >
          {isSuccess
            ? 'ดำเนินการเรียบร้อย'
            : isLoading
            ? 'กำลังดำเนินการ...'
            : !isRecaptchaValidated
            ? 'กรุณารอสักครู่...'
            : 'ติดต่อเรา'}
        </button>

        <PrivacyNotice name='privacy-notice' className='Contact__form__text' />
      </form>
    </React.Fragment>
  )
}

ContactForm.propTypes = {
  isRecaptchaValidated: PropTypes.bool,
}

export default ContactForm
